<template>
  <div>
    <!-- selected address details -->
    <div
      v-if="
        showManualAddressForm &&
        !isShowAddressForm &&
        isAddressSelected &&
        !loading
      "
      :class="addressSummaryWrapper"
    >
      <h3 class="font-18">{{ manualAddressFormText }}</h3>
      <p v-if="address.Street.Value">
        {{ address.Street.Value }}
      </p>
      <p v-if="address.District.Value">
        {{ address.District.Value }}
      </p>
      <p v-if="address.Town.Value">
        {{ address.Town.Value }}
      </p>
      <p v-if="address.County.Value">
        {{ address.County.Value }}
      </p>
      <p v-if="address.PostCode.Value">
        {{ address.PostCode.Value }}
      </p>
      <a
        href="javascript:"
        class="d-inline-flex mt-2 font-weight-semi-bold accent--text text-cursor psdarkgreen--text"
        @click="toggleAddressManualEntryForm(true)"
        >Edit</a
      >
    </div>

    <!-- address search input -->
    <template
      v-if="
        !showManualAddressForm ||
        (showManualAddressForm && !isShowAddressForm && !isAddressSelected) ||
        (showManualAddressForm && isAddressSelected && loading)
      "
    >
      <ValidationProvider
        :rules="rules"
        tag="div"
        v-slot="{ errors, valid, validated, invalid }"
      >
        <v-autocomplete
          class="find-property"
          :data-uiid="dataUiid"
          v-model="selectedValue"
          dense
          outlined
          :items="items"
          :loading="isLoading"
          :search-input.sync="searchInput"
          no-filter
          @click:clear="clearInput"
          @keyup.delete="$emit('keyup.delete')"
          item-text="Description"
          item-value="Text"
          :placeholder="placeholder ?? 'Start typing...'"
          :id="id"
          :error-messages="errorMessage || errors[0]"
          :class="[{ 'field-invalid': invalid }]"
          :append-icon="displayValidationIcon(errorMessage, validated)"
          return-object
          ref="autocomplete"
          :hide-details="hideDetails"
          open-on-clear
          :height="height"
          clearable
          :hide-no-data="isShowNoData"
          @blur="onBlurCheckAddressSelected()"
          aria-label="Address lookup"
          :menu-props="menuProps"
        >
          <template v-if="$refs.autocomplete" v-slot:append>
            <v-icon
              v-if="valid && validated"
              color="#1d8649"
              size="20"
              :class="[iconStyleClass]"
              >mdi-check-circle</v-icon
            >
            <v-icon
              v-if="!valid && validated"
              color="var(--red-500)"
              size="20"
              :class="[iconStyleClass]"
              >mdi-alert-circle</v-icon
            >
          </template>

          <template v-slot:append-item v-if="items.length">
            <v-list-item class="hasClick">
              <v-list-item-content @click="enterAddressManually">
                <v-list-item-title
                  ><span class="text-decoration-underline"
                    >Can't find your address? Enter manually</span
                  ></v-list-item-title
                >
              </v-list-item-content>
            </v-list-item>
          </template>

          <template v-slot:no-data>
            <v-list-item class="noHover">
              <v-list-item-content>
                <v-list-item-title>
                  <p class="font-14">
                    <strong>Sorry!</strong> We can't find the address right
                    now...
                  </p>
                  <p class="text-center mt-6" @click="enterAddressManually">
                    <span class="text-decoration-underline font-16 hasClick"
                      >Enter address manually</span
                    >
                  </p></v-list-item-title
                >
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-autocomplete>
      </ValidationProvider>
    </template>

    <!-- manual address form -->
    <AddressForm
      v-if="showManualAddressForm && isShowAddressForm"
      :addressData="address"
      :dataUiid="manualFormUiid"
      :hideDetails="hideDetails"
      :maxLengthStreet="maxLengthStreet"
      @useAddressFinder="useAddressFinder"
      @formSubmitted="formSubmitted"
    />
  </div>
</template>

<script>
import { API_URL_ADDRESSY } from "@/constants/api.constants.js";
import axios from "axios";
import format from "string-format";
import { ValidationProvider } from "vee-validate";

import { default as AddressForm } from "@/components/BaseComponents/BasePropertyDetailsLookup/AddressManualEntryForm";

export default {
  name: "BaseAddressLookup",
  components: {
    ValidationProvider,
    AddressForm,
  },
  props: {
    value: String,
    errorMessage: String,
    iconStyleClass: String,
    addressSummaryWrapper: {
      type: String,
      default: "mt-12 address-summary__wrapper",
    },
    hideValidationIcon: Boolean,
    rules: {
      type: Object,
    },
    placeholder: {
      type: String,
      default: "Start typing...",
    },
    dataUiid: String,
    manualFormUiid: String,
    loading: Boolean,
    height: String,
    hideDetails: {
      type: [String, Boolean],
      default: "auto",
    },
    address: {
      type: Object,
      default: () => {},
    },
    showManualAddressForm: {
      type: Boolean,
      default: false,
    },
    manualAddressFormText: {
      type: String,
      default: "",
    },
    id: {
      type: String,
      required: false,
      default: "address-lookup",
    },
    maxLengthStreet: {
      type: Number,
      default: 30,
    },
    menuProps: {
      type: Object,
      default: () => {
        return {
          bottom: true,
          offsetY: true,
          allowOverflow: true,
          offsetOverflow: false, // required for v-autocomplete known Vuetify bug
          contentClass: "autocomplete-dropdown",
        };
      },
    },
  },
  data: () => ({
    entries: [],
    isLoading: false,
    selectedValue: null,
    search: null,
    debounce: null,
    debounceTimeout: null,
    isShowAddressForm: false,
    isAddressSelected: false,
    // mark whether 'value' params is not null and should be treated as a special case in
    // search address logic
    isFirstSearchInitialAddress: false,
  }),
  computed: {
    searchInput: {
      //when selectedValue is set on created hook, it will trigger search-input which will clear entries/items,
      //return the value instead to stop watcher for debounce to fire to keep the entries on created hook.
      get: function () {
        return this.value ? this.value : this.debounce;
      },
      set: function (newVal) {
        this.debounce = newVal;
      },
    },
    items() {
      if (this.entries.length === 1 && this.entries[0].Error) {
        return [];
      }

      return this.entries.map((entry) => {
        if (entry?.Type === "Container") {
          entry.Text += entry.Description;
        }
        entry.Description = entry.Text;

        return entry;
      });
    },
    isShowNoData() {
      return this.isLoading || !this.searchInput;
    },
  },
  created() {
    // v-autocomplete only shows input value if it is available in the items prop.
    // add the value to selectedValue and items/entries to set the v-autocomplete on initial render if passed as prop.
    if (this.value) {
      this.selectedValue = {
        Description: this.value,
        Text: this.value,
      };
      this.entries.push(this.selectedValue);
      this.isFirstSearchInitialAddress = true;
    }

    //If street address is available, show the selected address
    if (this.address?.Street?.Value) {
      this.isAddressSelected = true;
    }
  },
  watch: {
    debounce(val) {
      this.isLoading = true;
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.searchAddress(val);
      }, 500);
    },
    loading() {
      this.isLoading = this.loading;
    },
    isAddressSelected(val) {
      this.$emit("is-address-selected", val);
    },
  },
  methods: {
    searchAddress(val) {
      if (val !== this.selectedValue?.Text && this.selectedValue?.Type)
        this.selectedValue.Type = null; //When user is editing address, set selectedValue type to null so fresh result could be fetched
      if (!val) {
        this.isLoading = false;
        this.entries = [];
        return;
      }

      const addressyParams = {
        callType: "Find",
        text: val,
        container: "",
        id: "",
      };

      if (this.selectedValue?.Type === "Address") {
        addressyParams.callType = "Retrieve";
        addressyParams.id = this.selectedValue.Id;
        this.fetchAddressFromApi(addressyParams).then((data) => {
          this.isAddressSelected = true;
          this.isShowAddressForm = false;

          this.$emit("address-selected", { data });
          this.$emit("is-complete-fill-address", true);
        });
        return;
      }

      if (this.selectedValue?.Type === "Container") {
        addressyParams.container = this.selectedValue.Id;
        this.fetchAddressFromApi(addressyParams).then((data) => {
          this.isAddressSelected = false;
          this.isShowAddressForm = false;
          this.entries = data.Items;
        });
        return;
      }

      this.fetchAddressFromApi(addressyParams).then((data) => {
        this.entries = data.Items;
        if (
          !this.isFirstSearchInitialAddress ||
          this.entries.length !== 1 ||
          this.entries[0].Type !== "Address"
        ) {
          return;
        }
        // handle first time component load, and with value of address not null
        // if search is successful and get only 1 address then we assign that to the selectedValue and debounce
        this.selectedValue = this.entries[0];
        this.debounce = this.selectedValue.Text;
        this.isFirstSearchInitialAddress = false;
      });
    },
    async fetchAddressFromApi(params) {
      this.isLoading = true;
      return axios
        .get(format(API_URL_ADDRESSY, ...Object.values(params)))
        .then((res) => {
          if (params.container) {
            if (this.dataUiid) {
              document.querySelector(`[data-uiid=${this.dataUiid}]`)?.click();
            } else {
              document.getElementById(this.id).click();
            }
          }
          return res.data;
        })
        .catch(() => {
          if (this.showManualAddressForm) {
            // check if the showManualAddressForm prop was provided
            this.isAddressSelected = false;
            this.isShowAddressForm = true;

            this.enterAddressManually();
          } else {
            this.$emit("enter-address-manually");
          }
        })
        .finally(() => (this.isLoading = false));
    },
    displayValidationIcon(errorMessage, validated) {
      if (this.hideValidationIcon) {
        return "";
      }
      return errorMessage ? "" : validated ? "icon-check" : "";
    },
    useAddressFinder() {
      this.isAddressSelected = false;
      this.isShowAddressForm = false;

      this.address.Street.Value = "";
      this.address.District.Value = "";
      this.address.Town.Value = "";
      this.address.County.Value = "";
      this.address.PostCode.Value = "";

      this.entries = [];
      this.selectedValue = null;
      this.search = null;
      this.debounce = null;

      this.$emit("use-address-finder");
    },
    formSubmitted(manualAddressData) {
      for (const key in manualAddressData) {
        this.address[key].Value = manualAddressData[key].Value;
      }
      this.isAddressSelected = true;
      this.isShowAddressForm = false;
      this.$emit("is-complete-fill-address", true);
    },
    toggleAddressManualEntryForm(value) {
      this.isAddressSelected = true;
      this.isShowAddressForm = value;
      this.$emit("enter-address-manually");
      this.$emit("is-complete-fill-address", false);
    },
    clearInput() {
      // extra logic needed to clear all entries on the first time value prop has data
      // because the data in the entries will remain here if we do not clear it, due to
      // logic related to debounce value not changing
      if (this.isFirstSearchInitialAddress) {
        this.entries = [];
        this.isFirstSearchInitialAddress = false;
      }
      this.$emit("click:clear");
    },
    enterAddressManually() {
      this.isAddressSelected = false;
      this.isShowAddressForm = true;
      this.$emit("enter-address-manually");
    },
    onBlurCheckAddressSelected() {
      if (this.selectedValue?.Type === "Container") {
        this.selectedValue = null;
        this.$emit("click:clear");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.address-summary__wrapper {
  border-left: solid 2px #106175;
  padding-left: 24px;
  margin-bottom: 24px;
  p,
  h4 {
    margin: 0;
    font-size: 18px;
    color: $black-2;
  }
}

.hasClick {
  cursor: pointer !important;
}
.theme--light.v-list-item.noHover:hover {
  background: transparent !important;
  color: rgba(0, 0, 0, 0.87) !important;
}
.description {
  letter-spacing: 0.4px;
  margin-bottom: 13px;
  max-width: 665px;
  color: #272727;
}
.icon-mt {
  margin-top: 1px !important;
}

:deep(.v-select__slot) {
  input::placeholder {
    font-style: italic;
  }
}

:deep(.v-input__append-inner) {
  margin: auto 0 !important;
  .v-input__icon--clear {
    width: 20px;
    height: 20px;
  }
}
</style>
